import { datadogRum } from '@datadog/browser-rum';
import { Redirect, Router } from '@reach/router';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import { DateTime } from 'luxon';
import { includes } from 'ramda';
import React from 'react';
import { useAsync } from 'react-use';

import AppointmentBookingRoute from '@ui/routes/AppointmentBooking/AppointmentBookingRoute';
import AppointmentConfirmationErrorStep from '@ui/routes/AppointmentBooking/AppointmentConfirmationErrorStep';
import ConfirmedPaymentNotRequiredStep from '@ui/routes/AppointmentBooking/ConfirmedPaymentNotRequiredStep';
import GenericErrorStep from '@ui/routes/AppointmentBooking/GenericErrorStep';
import NewAppointmentBookedStep from '@ui/routes/AppointmentBooking/NewAppointmentBookedStep';
import SelectAppointmentStep from '@ui/routes/AppointmentBooking/SelectAppointmentStep';
import AccountInfo from '@ui/routes/Dashboard/Account/AccountInfo';
import AccountInfoEditPage from '@ui/routes/Dashboard/Account/AccountInfoEditStep';
import AccountNameEdit from '@ui/routes/Dashboard/Account/AccountNameEdit';
import LegalInfoEdit from '@ui/routes/Dashboard/Account/LegalInfoEditStep';
import AppointmentsPage from '@ui/routes/Dashboard/Appointments/AppointmentsInfo';
import RescheduleAppointmentPage from '@ui/routes/Dashboard/Appointments/RescheduleAppointment';
import BillingCancelMembershipPage from '@ui/routes/Dashboard/Billing/BillingCancelMembership';
import BillingPage from '@ui/routes/Dashboard/Billing/BillingInfo';
import BillingRoute from '@ui/routes/Dashboard/Billing/BillingRoute/BillingRoute.component';
import PaymentEditPage from '@ui/routes/Dashboard/Billing/PaymentEditStep';
import ShippingEdit from '@ui/routes/Dashboard/Billing/ShippingEditStep';
import BillDetailsPage from '@ui/routes/Dashboard/Bills/BillDetails';
import BillPayRedirect from '@ui/routes/Dashboard/Bills/BillPayRedirect';
import BillsListStep from '@ui/routes/Dashboard/Bills/BillsListStep';
import BillsRoute from '@ui/routes/Dashboard/Bills/BillsRoute/BillsRoute.component';
import MedicationBillDetailsPage from '@ui/routes/Dashboard/Bills/MedicationBillsDetailStep';
import DashboardRoute from '@ui/routes/Dashboard/DashboardRoute';
import DependentInvite from '@ui/routes/Dashboard/Dependents/DependentInvite';
import DependentsList from '@ui/routes/Dashboard/Dependents/DependentsList';
import EmergencyContactsEdit from '@ui/routes/Dashboard/EmergencyContacts/EmergencyContactsEditStep';
import EmergencyContacts from '@ui/routes/Dashboard/EmergencyContacts/EmergencyContactsInfo';
import GetCarePage from '@ui/routes/Dashboard/GetCare';
import DashboardInsuranceConfirmationStep from '@ui/routes/Dashboard/Insurance/InsuranceConfirmationStep';
import DashboardInsuranceCoverageStep from '@ui/routes/Dashboard/Insurance/InsuranceCoverageStep';
import DashboardInsuranceDetailsStep, {
	InsuranceDetailsEditStep as DashboardInsuranceDetailsEditStep,
} from '@ui/routes/Dashboard/Insurance/InsuranceDetailsStep';
import DashboardInsuranceOutOfNetworkStep from '@ui/routes/Dashboard/Insurance/InsuranceOutOfNetworkStep';
import DashboardInsuranceProviderStep from '@ui/routes/Dashboard/Insurance/InsuranceProviderStep';
import DashboardInsuranceRemoveConfirmationStep from '@ui/routes/Dashboard/Insurance/InsuranceRemoveConfirmationStep';
import InsuranceRoute from '@ui/routes/Dashboard/Insurance/InsuranceRoute/InsuranceRoute.component';
import LabsPlaceholder from '@ui/routes/Dashboard/Labs/LabsPlaceholder';
import MedicationDeliveryEdit from '@ui/routes/Dashboard/Medication/MedicationDeliveryEditStep';
import MedicationDeliveryPostSubmitPreferredPharmacy from '@ui/routes/Dashboard/Medication/MedicationDeliveryPostSubmitPreferredPharmacyStep';
import MedicationDeliveryPostSubmit from '@ui/routes/Dashboard/Medication/MedicationDeliveryPostSubmitStep';
import MedicationNextSteps from '@ui/routes/Dashboard/Medication/MedicationNextSteps';
import MemberHomePage from '@ui/routes/Dashboard/MemberHomeStep';
import MemberOnlyRoute from '@ui/routes/Dashboard/MemberOnlyRoute';
import CancelationConfirmation from '@ui/routes/Dashboard/Membership/CancelationConfirmation';
import CancelationSurvey from '@ui/routes/Dashboard/Membership/CancelationSurvey';
import CancelationWinback from '@ui/routes/Dashboard/Membership/CancelationWinback';
import ReactivateMembershipPage from '@ui/routes/Dashboard/Membership/ReactivateMembership';
import ReactivateMembershipSuccessPage from '@ui/routes/Dashboard/Membership/ReactivateMembershipSuccess';
import ReactivateMembershipSummaryPage from '@ui/routes/Dashboard/Membership/ReactivateMembershipSummary';
import SwitchToMonthlyMembershipPage from '@ui/routes/Dashboard/Membership/SwitchToMonthlyMembership';
import SwitchToYearlyMembershipPage from '@ui/routes/Dashboard/Membership/SwitchToYearlyMembership';
import UnpaidMembership from '@ui/routes/Dashboard/Membership/UnpaidMembership';
import Messages from '@ui/routes/Dashboard/MessagesStep';
import ErrorPage from '@ui/routes/Error/Error.page';
import ErrorRoute from '@ui/routes/Error/ErrorRoute';
import GahtFuAppointmentSelectionStep from '@ui/routes/GahtFu/GahtFuAppointmentSelectionStep';
import GahtFuOrderSummaryStep from '@ui/routes/GahtFu/GahtFuOrderSummaryStep';
import GahtFuRoute from '@ui/routes/GahtFu/GahtFuRoute';
import GahtFuUnsubscribedStep from '@ui/routes/GahtFu/GahtFuUnsubscribedStep';
import InsuranceDetailsStep, {
	InsuranceDetailsEditStep,
} from '@ui/routes/Insurance/InsuranceDetailsStep';
import InsuranceOrderSummaryStep from '@ui/routes/Insurance/InsuranceOrderSummaryStep';
import InsurancePromptStep from '@ui/routes/Insurance/InsurancePromptStep';
import InsuranceProviderStep from '@ui/routes/Insurance/InsuranceProviderStep';
import InsuranceFlowRoute from '@ui/routes/Insurance/InsuranceRoute/InsuranceFlowRoute.component';
import InsuranceSelectionStep from '@ui/routes/Insurance/InsuranceSelectionStep';
import MemberAdhdDiagnosisStep from '@ui/routes/MemberADHD/MemberAdhdDiagnosisStep';
import MemberAdhdInstructionsStep from '@ui/routes/MemberADHD/MemberAdhdInstructionsStep';
import EstrogenInformedConsentStep from '@ui/routes/MemberEstrogen/InformedConsentStep';
import MECisgenderIneligibilityNoticeStepPage from '@ui/routes/MemberEstrogen/MECisgenderIneligibilityNoticeStep';
import MemberEstrogenGoalsStep from '@ui/routes/MemberEstrogen/MemberEstrogenGoalsStep';
import MemberEstrogenMedicationSelectionStep from '@ui/routes/MemberEstrogen/MemberEstrogenMedicationSelectionStep';
import MemberEstrogenOtherMedicationStep from '@ui/routes/MemberEstrogen/MemberEstrogenOtherMedicationStep';
import MemberEstrogenRoute from '@ui/routes/MemberEstrogen/MemberEstrogenRoute';
import MemberGAHTConsultGetCareStep from '@ui/routes/MemberGAHTConsult/MemberGAHTConsultGetCareStep';
import MemberGAHTConsultRoute from '@ui/routes/MemberGAHTConsult/MemberGAHTConsultRoute';
import MHExceptionalConditionsStepPage from '@ui/routes/MemberMH/MHExceptionalConditionsStep';
import MHIneligibleExceptionsStep from '@ui/routes/MemberMH/MHIneligibleExceptionsStep';
import MHPostSubmitStep from '@ui/routes/MemberMH/MHPostSubmitStep';
import MHIntakeIntroStep from '@ui/routes/MemberMH/MemberMHIntakeIntroStep';
import MemberMHNextStepsStep from '@ui/routes/MemberMH/MemberMHNextStepsStep';
import MemberMHRoute from '@ui/routes/MemberMH/MemberMHRoute';
import MemberMHTopicSelectionStep, {
	mentalHealthTopicConstants,
} from '@ui/routes/MemberMH/MemberMHTopicSelectionStep';
import DlInputStepPage from '@ui/routes/MemberTestosterone/DlInputStep';
import DlPromptStepPage from '@ui/routes/MemberTestosterone/DlPromptStep';
import TestosteroneInformedConsentStep from '@ui/routes/MemberTestosterone/InformedConsentStep';
import MTCisgenderIneligibilityNoticeStepPage from '@ui/routes/MemberTestosterone/MTCisgenderIneligibilityNoticeStep';
import MemberTestosteroneGoalsStep from '@ui/routes/MemberTestosterone/MemberTestosteroneGoalsStep';
import MemberTestosteroneMedicationSelectionStep from '@ui/routes/MemberTestosterone/MemberTestosteroneMedicationSelectionStep';
import MemberTestosteroneRoute from '@ui/routes/MemberTestosterone/MemberTestosteroneRoute';
import SsnDlExceptionStepPage from '@ui/routes/MemberTestosterone/SsnDlExceptionStep';
import SsnInputStepPage from '@ui/routes/MemberTestosterone/SsnInputStep';
import SsnPromptStepPage from '@ui/routes/MemberTestosterone/SsnPromptStep';
import MemberVHCAnythingElseStepPage from '@ui/routes/MemberVHC/MemberVHCAnythingElseStep/MemberVHCAnythingElseStep.page';
import MemberVHCGetCareStepPage from '@ui/routes/MemberVHC/MemberVHCGetCareStep/MemberVHCGetCareStep.page';
import MemberVHCRoute from '@ui/routes/MemberVHC/MemberVHCRoute';
import PostSubmitStep from '@ui/routes/MemberVHC/PostSubmitStep';
import AgeIneligibilityStep from '@ui/routes/Membership/AgeIneligibilityStep';
import HomeAddressStep from '@ui/routes/Membership/HomeAddressStep';
import InterestCaptureStep from '@ui/routes/Membership/InterestCaptureStep';
import MedicaidIneligibilityStep from '@ui/routes/Membership/MedicaidIneligibilityStep';
import MedicareIneligibilityStep from '@ui/routes/Membership/MedicareIneligibilityStep';
import MemberIdInfoStepPage from '@ui/routes/Membership/MemberIdInfoStep';
import MemberUploadIdStep from '@ui/routes/Membership/MemberUploadIdStep';
import MemberUploadSelfieStep from '@ui/routes/Membership/MemberUploadSelfieStep';
import MembershipAuthStep from '@ui/routes/Membership/MembershipAuthStep/MembershipAuthStep.page';
import MembershipBasicInfoStep from '@ui/routes/Membership/MembershipBasicInfoStep';
import MembershipCheckEmailStep from '@ui/routes/Membership/MembershipCheckEmailStep';
import MembershipInsuranceCoverageStep from '@ui/routes/Membership/MembershipInsuranceCoverageStep';
import { dataConstants as insuranceCoverageDataConstants } from '@ui/routes/Membership/MembershipInsuranceCoverageStep/MembershipInsuranceCoverageStep.data';
import MembershipInsuranceDetailsStep from '@ui/routes/Membership/MembershipInsuranceDetailsStep';
import MembershipInsuranceProviderStep from '@ui/routes/Membership/MembershipInsuranceProviderStep';
import MembershipInsuranceStatusStep from '@ui/routes/Membership/MembershipInsuranceStatusStep';
import { dataConstants as insuranceStatusConstants } from '@ui/routes/Membership/MembershipInsuranceStatusStep/MembershipInsuranceStatusStep.data';
import MembershipRoute from '@ui/routes/Membership/MembershipRoute';
import MembershipSelectionStep from '@ui/routes/Membership/MembershipSelectionStep';
import MembershipSetPasswordStep from '@ui/routes/Membership/MembershipUserSetPasswordStep';
import MembershipNextStepsStep from '@ui/routes/Membership/NextStepsStep';
import PersonalInfoStep from '@ui/routes/Membership/PersonalInfoStep';
import StateIneligibilityStep from '@ui/routes/Membership/StateIneligibilityStep';
import SignIn from '@ui/routes/SignIn';
import AdditionalInfoStep from '@ui/routes/shared/AdditionalInfoStep';
import DeliveryConfirmationStep from '@ui/routes/shared/DeliveryConfirmationStep';
import EmergencyContactsEditStep from '@ui/routes/shared/EmergencyContactsEditStep';
import ImageUploadErrorStep from '@ui/routes/shared/ImageUploadErrorStep';
import IntakeIntroStep from '@ui/routes/shared/IntakeIntroStep';
import MedicaidAttestationStepPage from '@ui/routes/shared/MedicaidAttestationStep';
import MedicareAttestationStepPage from '@ui/routes/shared/MedicareAttestationStep';
import MedicationDeliverySelection, {
	medicationDeliveryConstants,
} from '@ui/routes/shared/MedicationDeliveryStep';
import MemberAlreadySubscribedStep from '@ui/routes/shared/MemberAlreadySubscribedStep';
import MemberAppointmentInfoStep from '@ui/routes/shared/MemberAppointmentInfoStep';
import MemberAppointmentSelection from '@ui/routes/shared/MemberAppointmentSelectionStep';
import MemberStateIneligibilityStep from '@ui/routes/shared/MemberStateIneligibilityStep';
import MembershipCheckPage from '@ui/routes/shared/MembershipCheck';
import MembershipInsuranceFeatureFlagParentRoute from '@ui/routes/shared/MembershipInsuranceFeatureFlagParentRoute';
import NewMemberNextStepsStep from '@ui/routes/shared/NewMemberNextStepsStep';
import ParentRoute from '@ui/routes/shared/ParentRoute';
import PharmacySelection from '@ui/routes/shared/PharmacySelectionStep';
import StateRestrictionStep from '@ui/routes/shared/StateRestrictionStep';

import { getServiceType } from '@helpers/MentalHealth/mentalHealth';

import { exists } from '@utils/general';
import { isBrowser } from '@utils/isBrowser';
import { getAllStateServiceAvailability } from '@utils/service-availability';

import { ExemptionType } from '@data/insurance';
import { MHTopic, MHTopicId } from '@data/mh_topics';

import { FormData } from '@folx-types/flow';

import AppointmentBookedRedirect from '@components/AppointmentBookedRedirect';
import AppointmentScheduled from '@components/AppointmentScheduled';
import BookInPersonAppointmentPage from '@components/BookInPersonAppointment/BookInPersonAppointment.page';
import FfsMedPayment from '@components/FfsMedPayment';
import InnerspaceInterstitial from '@components/InnerspaceInterstitial';
import InnerspaceNickname from '@components/InnerspaceNickname';
import ManageAppointmentsPage from '@components/ManageAppointments/ManageAppointments.page';
import PrivateRoute from '@components/PrivateRoute';
import PublicRoute from '@components/PublicRoute';
import ResetPasswordWithCode from '@components/ResetPasswordWithCode/ResetPasswordWithCode';
import UpdatePayment from '@components/UpdatePayment';
import ZendeskSSO from '@components/ZendeskSSO';

const TopLevelRouter = () => {
	const stripePromise = loadStripe(process.env.GATSBY_STRIPE_PUBLISHABLE_KEY ?? 'foo');

	const { value: stateServiceMapping } = useAsync(
		async () => await getAllStateServiceAvailability()
	);
	console.log(stateServiceMapping);

	return (
		<Elements stripe={stripePromise}>
			<Router basepath="/app">
				<PublicRoute component={FfsMedPayment} path="/med_payment/:batchId" />
				<PublicRoute component={MedicationNextSteps} path="/med_purchase_success" />
				<PrivateRoute component={UpdatePayment} path="/update_payment" />
				<MemberOnlyRoute
					component={InnerspaceInterstitial}
					path="/innerspace"
					includeErrorContainer
					allowFolxMembers
				/>
				<PrivateRoute component={InnerspaceNickname} path="/innerspace_nickname" />
				<PrivateRoute component={ZendeskSSO} path="/zendesk_sso" />
				<Redirect from="/book_an_appointment" to="/app/member/appointments" noThrow />
				<Redirect from="/manage_appointments" to="/app/member/appointments" noThrow />
				<PrivateRoute component={ManageAppointmentsPage} path="/manage_appointments" />
				<PrivateRoute component={BookInPersonAppointmentPage} path="/book_in_person_appointment" />
				<PrivateRoute
					component={AppointmentBookedRedirect}
					path="/appointment_booked"
					addQueryParams
				/>
				<PrivateRoute component={AppointmentScheduled} path="/appointment_scheduled" />
				<Redirect from="/account" to="/app/member/account" noThrow />
				<Redirect from="/account/:pathname" to="/app/member/:pathname" noThrow />
				<Redirect
					from="/member/billing/bills/outstanding"
					to="/app/member/billing/bills"
					noThrow
					replace
				/>
				<DashboardRoute path="/member">
					<MemberHomePage path="/" />
					<AccountNameEdit path="/name_edit" />
					<UnpaidMembership path="/unpaid" />
					<AccountInfo path="/account" />
					<AccountInfoEditPage path="/account/info_edit" />
					<LegalInfoEdit path="/account/legal_edit" />
					<EmergencyContacts path="/account/contacts" />
					<EmergencyContactsEdit path="/account/contacts/edit" />
					<BillingRoute path="/billing">
						<BillingPage path="/" />
						<SwitchToYearlyMembershipPage path="/switch_to_yearly_membership" />
						<SwitchToMonthlyMembershipPage path="/switch_to_monthly_membership" />
						<CancelationConfirmation path="/cancel_membership" />
						<BillingCancelMembershipPage path="/appointments_past_cancel_date" />
						<CancelationWinback path="/cancel_winback" />
						<CancelationSurvey path="/cancel_survey" />
						<MedicationDeliveryEdit path="/delivery_edit" />
						<MedicationDeliveryPostSubmitPreferredPharmacy path="/delivery_edit/post_submit_preferred_pharmacy" />
						<MedicationDeliveryPostSubmit path="/delivery_edit/post_submit" />
						<PaymentEditPage path="/payment_edit" />
						<ShippingEdit path="/shipping_edit" />
						<InsuranceRoute path="/insurance">
							<DashboardInsuranceProviderStep path="/provider" />
							<DashboardInsuranceDetailsStep path="/details" />
							<DashboardInsuranceOutOfNetworkStep path="/out_of_network" />
							<DashboardInsuranceConfirmationStep path="/confirmation" />
							<DashboardInsuranceCoverageStep path="/coverage" />
							<DashboardInsuranceDetailsEditStep path=":memberInsuranceInfoId/insurance_details_edit/" />
							<DashboardInsuranceRemoveConfirmationStep path=":memberInsuranceInfoId/remove_confirmation" />
						</InsuranceRoute>
						<BillsRoute path="/bills">
							<BillsListStep path="/" />
							<BillDetailsPage path="/invoice/:invoiceId" />
							<MedicationBillDetailsPage path="/details/:billId/" />
							<BillPayRedirect path="/paid" />
						</BillsRoute>
					</BillingRoute>
					<GetCarePage path="/get_care" />
					<ReactivateMembershipPage path="/reactivate_membership" />
					<ReactivateMembershipSummaryPage path="/reactivate_membership/summary" />
					<ReactivateMembershipSuccessPage path="/reactivate_membership/success" />
					<MedicationNextSteps path="/order/medication/confirm" />
					<DependentsList path="/dependents" />
					<DependentInvite path="/dependents/invite" />
					<Messages path="/messages" />
					<AppointmentsPage path="/appointments" />
					<RescheduleAppointmentPage path="/appointments/reschedule/:appointmentId" />
					<LabsPlaceholder path="/labs" />
				</DashboardRoute>
				<ErrorRoute path="/error">
					<ErrorPage path="/" />
					<ErrorPage path="/not-found" />
				</ErrorRoute>
				<PublicRoute component={ResetPasswordWithCode} path="/reset_with_code" />
				<PublicRoute component={SignIn} path="sign_in" />
				<MembershipRoute path="/flows/membership">
					<MembershipBasicInfoStep path="eligibility" stepKey="ELIGIBILITY" />
					<MembershipCheckEmailStep path="check_email" stepKey="CHECK_EMAIL" intermediate />
					<MembershipAuthStep path="confirm/:registrationCode" stepKey="AUTH" intermediate />
					<InterestCaptureStep path="interest_capture" stepKey="INTEREST_CAPTURE" secure />
					<PersonalInfoStep
						path="personal_info"
						stepKey="PERSONAL_INFO"
						secure
						bailout={(formData: FormData) => {
							const { dob } = formData as { dob: string };
							const over18 = DateTime.fromISO(dob).diffNow('years').negate().years >= 18;
							if (!over18) return 'AGE_INELIGIBILITY';
							return false;
						}}
					/>
					<MedicareIneligibilityStep
						path="medicare_ineligibility"
						stepKey="MEDICARE_INELIGIBILITY"
						secure
					/>
					<HomeAddressStep path="address" stepKey="HOME_ADDRESS" secure />
					<MedicaidIneligibilityStep
						path="medicaid_ineligibility"
						stepKey="MEDICAID_INELIGIBILITY"
						secure
					/>
					<MembershipInsuranceFeatureFlagParentRoute
						flowId="membership"
						stepKey="INSURANCE"
						path="insurance"
						backTo="medicaid_ineligibility"
						skipTo="order_summary"
						shouldEscapeRoute={(formData) => {
							if (
								formData?.[insuranceCoverageDataConstants.insuranceCoverage] !==
								insuranceCoverageDataConstants.check
							) {
								return true;
							}

							const isWaivable = formData?.[insuranceStatusConstants.isWaivable];
							const exemption = formData?.[insuranceStatusConstants.exemptBy];
							if (
								isWaivable === true &&
								(exemption === ExemptionType.waiver || exemption === ExemptionType.employer)
							) {
								return true;
							}

							return false;
						}}>
						<MembershipInsuranceCoverageStep stepKey="INSURANCE_COVERAGE" path="coverage" secure />
						<MembershipInsuranceProviderStep stepKey="INSURANCE_PROVIDER" path="provider" secure />
						<MembershipInsuranceDetailsStep stepKey="INSURANCE_DETAILS" path="details" secure />
						<MembershipInsuranceStatusStep path="status" stepKey="INSURANCE_STATUS" secure />
					</MembershipInsuranceFeatureFlagParentRoute>
					<MembershipSelectionStep path="order_summary" stepKey="ORDER_SUMMARY" secure />
					<MembershipNextStepsStep path="success" stepKey="SUCCESS" secure finalStep />
					<StateIneligibilityStep
						path="ineligible_state"
						stepKey="STATE_INELIGIBILITY"
						returnKey="ELIGIBILITY"
						secure
					/>
					<AgeIneligibilityStep
						path="age_ineligible"
						stepKey="AGE_INELIGIBILITY"
						returnKey="ELIGIBILITY"
						secure
					/>
					<ImageUploadErrorStep stepKey="IMAGE_UPLOAD_ERROR" path="image_upload_error" secure />
				</MembershipRoute>
				<MemberMHRoute path="/flows/mh_sub">
					<MHIntakeIntroStep path="intro" stepKey="INTAKE_INTRODUCTION" onboardingStep secure />
					<MemberMHTopicSelectionStep
						path="topic"
						stepKey="TOPIC_SELECTION"
						onboardingStep
						secure
					/>
					<ParentRoute
						onboardingStep
						flowId="mh_sub"
						stepKey="ADHD"
						path="adhd"
						backTo="topic"
						skipTo="medicare"
						skip={({ formData }) => {
							return formData?.[mentalHealthTopicConstants.TOPIC_ID] !== MHTopic.ADHD;
						}}>
						<MemberAdhdDiagnosisStep
							path="diagnosis"
							stepKey="ADHD_DIAGNOSIS"
							onboardingStep
							secure
						/>
						<MemberAdhdInstructionsStep
							path="instructions"
							stepKey="ADHD_INSTRUCTIONS"
							onboardingStep
							secure
						/>
					</ParentRoute>
					<MedicareAttestationStepPage stepKey="MEDICARE_ATTESTATION" path="medicare" />
					<MedicaidAttestationStepPage stepKey="MEDICAID_ATTESTATION" path="medicaid" />
					<MemberIdInfoStepPage path="id_info" stepKey="ID_INFO" onboardingStep secure />
					<MemberUploadIdStep path="upload_id" stepKey="UPLOAD_ID" onboardingStep secure />
					<MemberUploadSelfieStep
						path="upload_selfie"
						stepKey="UPLOAD_SELFIE"
						onboardingStep
						secure
					/>
					<MHExceptionalConditionsStepPage
						stepKey="EXCEPTIONAL_CONDITIONS"
						path="exceptional_conditions"
						onboardingStep
						secure
						bailout={(formData: FormData) => {
							console.log('data', formData);
							const { suicideRisk } = formData;
							if (suicideRisk) return 'INELIGIBLE_EXCEPTIONS';
							return false;
						}}
					/>
					<EmergencyContactsEditStep path="contacts" stepKey="EMERGENCY_CONTACTS" secure />
					<ParentRoute
						flowId="mh_sub"
						stepKey="DELIVERY"
						path="delivery"
						backTo="topic"
						skipTo="delivery_confirmation"
						skip={({ formData }) => {
							const topicId = (formData?.[mentalHealthTopicConstants.TOPIC_ID] as string) ?? '';
							const serviceType = getServiceType(topicId, { vcFlow: true });
							return serviceType !== MHTopicId.vc;
						}}
						shouldEscapeRoute={(formData) => {
							return (
								formData?.[medicationDeliveryConstants.MEDICATION_DELIVERY_METHOD] !==
								medicationDeliveryConstants.PHARMACY_METHOD
							);
						}}>
						<MedicationDeliverySelection
							stepKey="DELIVERY_SELECTION"
							path="delivery_selection"
							secure
						/>
						<PharmacySelection stepKey="PHARMACY_SELECTION" path="pharmacy_selection" secure />
					</ParentRoute>
					<DeliveryConfirmationStep
						stepKey="DELIVERY_CONFIRM"
						path="delivery_confirmation"
						secure
					/>
					<MemberAppointmentInfoStep stepKey="APPOINTMENT_INFO" path="appointment_info" secure />
					<MemberAppointmentSelection
						path="appointment_selection"
						stepKey="APPOINTMENT_SELECTION"
						flowId="mh"
						secure
						bailout={(formData: FormData) => {
							const { isSubscriptionAppointment }: { isSubscriptionAppointment: boolean } =
								formData;
							return isSubscriptionAppointment && 'CONFIRMATION';
						}}
					/>
					<InsurancePromptStep stepKey="INSURANCE_PROMPT" path="insurance_prompt" skipIfWaived />
					<InsuranceFlowRoute
						path="insurance"
						stepKey="INSURANCE"
						flowId="mh_sub"
						skipTo="order_summary"
						backTo="insurance_prompt"
						skipIfWaived>
						<InsuranceSelectionStep stepKey="INSURANCE_SELECTION" path="insurance_selection" />
						<InsuranceProviderStep stepKey="INSURANCE_PROVIDER" path="insurance_provider" />
						<InsuranceDetailsEditStep
							stepKey="INSURANCE_DETAILS_EDIT"
							path="insurance_details_edit/:memberInsuranceInfoId"
						/>
						<InsuranceDetailsStep stepKey="INSURANCE_DETAILS" path="insurance_details" />
					</InsuranceFlowRoute>
					<InsuranceOrderSummaryStep
						stepKey="INSURANCE_ORDER_SUMMARY"
						path="order_summary"
						secure
						skipIfWaived
					/>
					<MHPostSubmitStep path="submit/*paymentState" stepKey="SUBMIT" secure skipIfWaived />
					<MemberMHNextStepsStep path="confirm" stepKey="NEXT_STEPS" secure finalStep />
					<MHIneligibleExceptionsStep
						stepKey="INELIGIBLE_EXCEPTIONS"
						path="ineligible_exceptions"
						secure
					/>
					<MemberStateIneligibilityStep
						path="ineligible_state"
						stepKey="STATE_INELIGIBILITY"
						returnKey="ELIGIBILITY"
					/>
					<MembershipCheckPage
						stepKey="CHECK_MEMBERSHIP"
						path="check_membership"
						onboardingStep
						secure
					/>
				</MemberMHRoute>
				<MemberEstrogenRoute path="/flows/hrt_e_sub">
					<IntakeIntroStep path="intro" stepKey="INTAKE_INTRODUCTION" onboardingStep secure />
					<MedicareAttestationStepPage stepKey="MEDICARE_ATTESTATION" path="medicare" />
					<MedicaidAttestationStepPage stepKey="MEDICAID_ATTESTATION" path="medicaid" />
					<MECisgenderIneligibilityNoticeStepPage
						path="cis_ineligibility"
						stepKey="CIS_INELIGIBILITY"
						onboardingStep
						secure
					/>
					<MemberIdInfoStepPage path="id_info" stepKey="ID_INFO" onboardingStep secure />
					<MemberUploadIdStep path="upload_id" stepKey="UPLOAD_ID" onboardingStep secure />
					<MemberUploadSelfieStep
						path="upload_selfie"
						stepKey="UPLOAD_SELFIE"
						onboardingStep
						secure
					/>
					<EmergencyContactsEditStep path="contacts" stepKey="EMERGENCY_CONTACTS" secure />
					<MemberEstrogenGoalsStep stepKey="ESTROGEN_GOALS" path="goals" onboardingStep secure />
					<MemberEstrogenMedicationSelectionStep
						stepKey="MEDICATION_SELECTION"
						path="medication_selection"
						secure
					/>
					<MemberEstrogenOtherMedicationStep
						stepKey="OTHER_MEDICATIONS"
						path="other_medications"
						secure
					/>
					<ParentRoute
						flowId="hrt_e_sub"
						stepKey="DELIVERY"
						path="delivery"
						backTo="other_medications"
						skipTo="delivery_confirmation"
						shouldEscapeRoute={(formData) => {
							return (
								formData[medicationDeliveryConstants.MEDICATION_DELIVERY_METHOD] !==
								medicationDeliveryConstants.PHARMACY_METHOD
							);
						}}>
						<MedicationDeliverySelection
							stepKey="DELIVERY_SELECTION"
							path="delivery_selection"
							secure
						/>
						<PharmacySelection stepKey="PHARMACY_SELECTION" path="pharmacy_selection" secure />
					</ParentRoute>
					<DeliveryConfirmationStep
						stepKey="DELIVERY_CONFIRM"
						path="delivery_confirmation"
						secure
					/>
					<EstrogenInformedConsentStep stepKey="INFORMED_CONSENT" path="informed_consent" secure />
					<MemberAppointmentInfoStep stepKey="APPOINTMENT_INFO" path="appointment_info" secure />
					<MemberAppointmentSelection
						stepKey="APPOINTMENT_SELECTION"
						path="appointment_selection"
						flowId="hrt_e"
						secure
					/>
					<AdditionalInfoStep stepKey="ADDITIONAL_INFO" path="additional_info" secure />
					<InsurancePromptStep stepKey="INSURANCE_PROMPT" path="insurance_prompt" skipIfWaived />
					<InsuranceFlowRoute
						path="insurance"
						stepKey="INSURANCE"
						flowId="hrt_e_sub"
						skipTo="order_summary"
						backTo="insurance_prompt"
						skipIfWaived>
						<InsuranceSelectionStep stepKey="INSURANCE_SELECTION" path="insurance_selection" />
						<InsuranceProviderStep stepKey="INSURANCE_PROVIDER" path="insurance_provider" />
						<InsuranceDetailsEditStep
							stepKey="INSURANCE_DETAILS_EDIT"
							path="insurance_details_edit/:memberInsuranceInfoId"
						/>
						<InsuranceDetailsStep stepKey="INSURANCE_DETAILS" path="insurance_details" />
					</InsuranceFlowRoute>
					<InsuranceOrderSummaryStep
						stepKey="INSURANCE_ORDER_SUMMARY"
						path="order_summary"
						secure
						skipIfWaived
					/>
					<PostSubmitStep path="submit/*paymentState" stepKey="SUBMIT" secure skipIfWaived />
					<NewMemberNextStepsStep stepKey="NEXT_STEPS" path="confirm" finalStep secure />
					<MemberAlreadySubscribedStep stepKey="ALREADY_SUBSCRIBED" path="subscribed" secure />
					<MemberStateIneligibilityStep
						path="ineligible_state"
						stepKey="STATE_INELIGIBILITY"
						returnKey="ELIGIBILITY"
					/>
					<StateRestrictionStep
						stepKey="STATE_RESTRICTION"
						path="state_restriction"
						onboardingStep
						secure
					/>
					<MembershipCheckPage
						stepKey="CHECK_MEMBERSHIP"
						path="check_membership"
						onboardingStep
						secure
					/>
				</MemberEstrogenRoute>
				<MemberVHCRoute path="/flows/vhc_sub">
					<MedicareAttestationStepPage stepKey="MEDICARE_ATTESTATION" path="medicare" />
					<MedicaidAttestationStepPage stepKey="MEDICAID_ATTESTATION" path="medicaid" />
					<MemberIdInfoStepPage path="id_info" stepKey="ID_INFO" onboardingStep secure />
					<MemberUploadIdStep path="upload_id" stepKey="UPLOAD_ID" onboardingStep secure />
					<MemberUploadSelfieStep
						path="upload_selfie"
						stepKey="UPLOAD_SELFIE"
						onboardingStep
						secure
					/>
					<MemberVHCGetCareStepPage path="get_care" stepKey="GET_CARE" onboardingStep secure />
					<EmergencyContactsEditStep
						path="contacts"
						stepKey="EMERGENCY_CONTACTS"
						onboardingStep
						secure
					/>
					<MemberVHCAnythingElseStepPage
						path="anything_else"
						stepKey="ANYTHING_ELSE"
						onboardingStep
						secure
					/>
					<ParentRoute
						flowId="vhc_sub"
						stepKey="DELIVERY"
						path="delivery"
						backTo="anything_else"
						skipTo="delivery_confirmation"
						shouldEscapeRoute={(formData) => {
							return (
								formData[medicationDeliveryConstants.MEDICATION_DELIVERY_METHOD] !==
								medicationDeliveryConstants.PHARMACY_METHOD
							);
						}}>
						<MedicationDeliverySelection
							stepKey="DELIVERY_SELECTION"
							path="delivery_selection"
							secure
						/>
						<PharmacySelection stepKey="PHARMACY_SELECTION" path="pharmacy_selection" secure />
					</ParentRoute>
					<DeliveryConfirmationStep
						stepKey="DELIVERY_CONFIRM"
						path="delivery_confirmation"
						secure
					/>
					<MemberAppointmentInfoStep stepKey="APPOINTMENT_INFO" path="appointment_info" secure />
					<MemberAppointmentSelection
						path="appointment_selection"
						stepKey="APPOINTMENT_SELECTION"
						flowId="vc"
						secure
						bailout={(formData: FormData) => {
							const { isSubscriptionAppointment }: { isSubscriptionAppointment: boolean } =
								formData;
							return isSubscriptionAppointment && 'CONFIRMATION';
						}}
					/>
					<InsurancePromptStep stepKey="INSURANCE_PROMPT" path="insurance_prompt" skipIfWaived />
					<InsuranceFlowRoute
						path="insurance"
						stepKey="INSURANCE"
						flowId="vhc_sub"
						skipTo="order_summary"
						backTo="insurance_prompt"
						skipIfWaived>
						<InsuranceSelectionStep stepKey="INSURANCE_SELECTION" path="insurance_selection" />
						<InsuranceProviderStep stepKey="INSURANCE_PROVIDER" path="insurance_provider" />
						<InsuranceDetailsEditStep
							stepKey="INSURANCE_DETAILS_EDIT"
							path="insurance_details_edit/:memberInsuranceInfoId"
						/>
						<InsuranceDetailsStep stepKey="INSURANCE_DETAILS" path="insurance_details" />
					</InsuranceFlowRoute>
					<InsuranceOrderSummaryStep
						stepKey="INSURANCE_ORDER_SUMMARY"
						path="order_summary"
						secure
						skipIfWaived
					/>
					<PostSubmitStep path="submit/*paymentState" stepKey="SUBMIT" secure skipIfWaived />
					<NewMemberNextStepsStep stepKey="CONFIRM" path="confirm" finalStep secure />
					<MemberStateIneligibilityStep
						path="ineligible_state"
						stepKey="STATE_INELIGIBILITY"
						returnKey="ELIGIBILITY"
					/>
					<MembershipCheckPage
						stepKey="CHECK_MEMBERSHIP"
						path="check_membership"
						onboardingStep
						secure
					/>
				</MemberVHCRoute>
				<GahtFuRoute path="/flows/followup">
					<MedicareAttestationStepPage stepKey="MEDICARE_ATTESTATION" path="medicare" />
					<MedicaidAttestationStepPage stepKey="MEDICAID_ATTESTATION" path="medicaid" />
					<EmergencyContactsEditStep path="contacts" stepKey="EMERGENCY_CONTACTS" secure />
					<MemberAppointmentInfoStep
						hideNavBack
						stepKey="APPOINTMENT_INFO"
						path="appointment_info"
						secure
					/>
					<GahtFuAppointmentSelectionStep
						stepKey="APPOINTMENT_SELECTION"
						path="appointment_selection"
						secure
					/>
					<AdditionalInfoStep stepKey="ADDITIONAL_INFO" path="additional_info" secure />
					<InsurancePromptStep stepKey="INSURANCE_PROMPT" path="insurance_prompt" skipIfWaived />
					<InsuranceFlowRoute
						path="insurance"
						stepKey="INSURANCE"
						flowId="followup"
						skipTo="order_summary"
						backTo="insurance_prompt"
						skipIfWaived>
						<InsuranceSelectionStep stepKey="INSURANCE_SELECTION" path="insurance_selection" />
						<InsuranceProviderStep stepKey="INSURANCE_PROVIDER" path="insurance_provider" />
						<InsuranceDetailsEditStep
							stepKey="INSURANCE_DETAILS_EDIT"
							path="insurance_details_edit/:memberInsuranceInfoId"
						/>
						<InsuranceDetailsStep stepKey="INSURANCE_DETAILS" path="insurance_details" />
					</InsuranceFlowRoute>
					<GahtFuOrderSummaryStep
						stepKey="ORDER_SUMMARY"
						path="order_summary"
						secure
						skipIfWaived
					/>
					<PostSubmitStep path="submit/*paymentState" stepKey="SUBMIT" secure skipIfWaived />
					<NewMemberNextStepsStep stepKey="NEXT_STEPS" path="confirm" finalStep secure />
					<GahtFuUnsubscribedStep stepKey="NOT_SUBSCRIBED" path="unsubscribed" secure />
					<StateRestrictionStep
						stepKey="STATE_RESTRICTION"
						path="state_restriction"
						onboardingStep
						secure
					/>
					<MemberStateIneligibilityStep
						path="ineligible_state"
						stepKey="STATE_INELIGIBILITY"
						returnKey="ELIGIBILITY"
					/>
				</GahtFuRoute>
				<MemberTestosteroneRoute path="/flows/hrt_t_sub">
					<IntakeIntroStep path="intro" stepKey="INTAKE_INTRODUCTION" onboardingStep secure />
					<MedicareAttestationStepPage stepKey="MEDICARE_ATTESTATION" path="medicare" />
					<MedicaidAttestationStepPage stepKey="MEDICAID_ATTESTATION" path="medicaid" />
					<MTCisgenderIneligibilityNoticeStepPage
						path="cis_ineligibility"
						stepKey="CIS_INELIGIBILITY"
						onboardingStep
						secure
					/>
					<MemberIdInfoStepPage path="id_info" stepKey="ID_INFO" onboardingStep secure />
					<MemberUploadIdStep path="upload_id" stepKey="UPLOAD_ID" onboardingStep secure />
					<MemberUploadSelfieStep
						path="upload_selfie"
						stepKey="UPLOAD_SELFIE"
						onboardingStep
						secure
					/>
					<ParentRoute
						flowId="hrt_t_sub"
						stepKey="SSN"
						path="ssn"
						skip={({ formData }) => {
							return formData.hasValidSsn === 'true';
						}}
						skipTo="dl"
						backTo="upload_id"
						onboardingStep>
						<SsnPromptStepPage
							path="ssn_start"
							stepKey="SSN_START"
							onboardingStep
							secure
							bailout={(formData: FormData) => {
								const { ssn } = formData;

								return ssn === '000-00-0000' && 'SSN_EXCEPTION';
							}}
						/>
						<SsnInputStepPage
							path="ssn_input"
							stepKey="SSN_INPUT"
							onboardingStep
							secure
							finalStep
						/>
						<SsnDlExceptionStepPage
							path="ssn_exception"
							stepKey="SSN_EXCEPTION"
							returnKey="SSN_START"
							onboardingStep
						/>
					</ParentRoute>
					<ParentRoute
						flowId="hrt_t_sub"
						stepKey="DL"
						path="dl"
						skip={({ formData, destinationFlowId, memberInfo }) => {
							let realFlowId = destinationFlowId;
							if (destinationFlowId === 'hrt_t_sub') realFlowId = 'hrt_t';

							let { stateOfResidence } = formData;

							console.log('INFO', memberInfo);

							stateOfResidence = memberInfo?.stateOfResidence?.abbreviation || stateOfResidence;

							if (realFlowId === 'hrt_t') {
								return !includes(stateOfResidence, ['UT', 'VA', 'KY']);
							}

							return true;
						}}
						skipTo="contacts"
						backTo="ssn"
						onboardingStep>
						<DlPromptStepPage
							path="dl_prompt"
							stepKey="DL_START"
							onboardingStep
							secure
							bailout={(formData: FormData) => {
								const { dln } = formData;

								return dln === 'NONE' && 'DL_EXCEPTION';
							}}
						/>
						<DlInputStepPage path="dl_input" stepKey="DL_INPUT" onboardingStep secure finalStep />
						<SsnDlExceptionStepPage
							path="dl_exception"
							stepKey="DL_EXCEPTION"
							returnKey="DL_START"
							onboardingStep
						/>
					</ParentRoute>
					<EmergencyContactsEditStep path="contacts" stepKey="EMERGENCY_CONTACTS" secure />
					<MemberTestosteroneGoalsStep
						stepKey="TESTOSTERONE_GOALS"
						path="goals"
						onboardingStep
						secure
					/>
					<MemberTestosteroneMedicationSelectionStep
						stepKey="MEDICATION_SELECTION"
						path="medication_selection"
						secure
					/>
					<ParentRoute
						flowId="hrt_t_sub"
						stepKey="DELIVERY"
						path="delivery"
						backTo="medication_selection"
						skipTo="delivery_confirmation"
						shouldEscapeRoute={(formData) => {
							return (
								formData[medicationDeliveryConstants.MEDICATION_DELIVERY_METHOD] !==
								medicationDeliveryConstants.PHARMACY_METHOD
							);
						}}>
						<MedicationDeliverySelection
							stepKey="DELIVERY_SELECTION"
							path="delivery_selection"
							secure
						/>
						<PharmacySelection stepKey="PHARMACY_SELECTION" path="pharmacy_selection" secure />
					</ParentRoute>
					<DeliveryConfirmationStep
						stepKey="DELIVERY_CONFIRM"
						path="delivery_confirmation"
						secure
					/>
					<TestosteroneInformedConsentStep
						stepKey="INFORMED_CONSENT"
						path="informed_consent"
						secure
					/>
					<MemberAppointmentInfoStep stepKey="APPOINTMENT_INFO" path="appointment_info" secure />
					<MemberAppointmentSelection
						stepKey="APPOINTMENT_SELECTION"
						path="appointment_selection"
						flowId="hrt_t"
						secure
					/>
					<AdditionalInfoStep stepKey="ADDITIONAL_INFO" path="additional_info" secure />
					<InsurancePromptStep stepKey="INSURANCE_PROMPT" path="insurance_prompt" skipIfWaived />
					<InsuranceFlowRoute
						path="insurance"
						stepKey="INSURANCE"
						flowId="hrt_t_sub"
						skipTo="order_summary"
						backTo="insurance_prompt"
						skipIfWaived>
						<InsuranceSelectionStep stepKey="INSURANCE_SELECTION" path="insurance_selection" />
						<InsuranceProviderStep stepKey="INSURANCE_PROVIDER" path="insurance_provider" />
						<InsuranceDetailsEditStep
							stepKey="INSURANCE_DETAILS_EDIT"
							path="insurance_details_edit/:memberInsuranceInfoId"
						/>
						<InsuranceDetailsStep stepKey="INSURANCE_DETAILS" path="insurance_details" />
					</InsuranceFlowRoute>
					<InsuranceOrderSummaryStep
						stepKey="INSURANCE_ORDER_SUMMARY"
						path="order_summary"
						secure
						skipIfWaived
					/>
					<PostSubmitStep path="submit/*paymentState" stepKey="SUBMIT" secure skipIfWaived />
					<NewMemberNextStepsStep stepKey="NEXT_STEPS" path="confirm" finalStep secure />
					<MemberAlreadySubscribedStep stepKey="ALREADY_SUBSCRIBED" path="subscribed" secure />
					<MemberStateIneligibilityStep
						path="ineligible_state"
						stepKey="STATE_INELIGIBILITY"
						returnKey="ELIGIBILITY"
					/>
					<StateRestrictionStep
						stepKey="STATE_RESTRICTION"
						path="state_restriction"
						onboardingStep
						secure
					/>
					<MembershipCheckPage
						stepKey="CHECK_MEMBERSHIP"
						path="check_membership"
						onboardingStep
						secure
					/>
				</MemberTestosteroneRoute>
				<MemberGAHTConsultRoute path="/flows/gaht_consult">
					<MemberGAHTConsultGetCareStep path="/" />
				</MemberGAHTConsultRoute>
				<AppointmentBookingRoute path="/flows/appointment_booking/:serviceType">
					<SelectAppointmentStep path="select_appointment" stepKey="SELECT" />
					<NewAppointmentBookedStep path="appointment_booked" stepKey="BOOKED" />
					<ConfirmedPaymentNotRequiredStep path="confirmed" stepKey="CONFIRMED_NO_PAYMENT" />
					<AppointmentConfirmationErrorStep path="not_confirmed" stepKey="NOT_CONFIRMED" />
					<GenericErrorStep path="error" stepKey="ERROR" />
				</AppointmentBookingRoute>
				<Redirect from="/flows/member" to="/app/member" noThrow replace />
			</Router>
		</Elements>
	);
};

const App = () => {
	// without this call RUM doesn't actually do anything with the sampled recording setting (besides charge us money)
	if (isBrowser) {
		datadogRum.startSessionReplayRecording();
	}

	return <TopLevelRouter />;
};

export default App;
